// pages/Users.js
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import userService from '../services/userService';
import { getIcon } from '../utils/icons';

/**
 * Страница управления пользователями
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const UsersPage = () => {
  const initialPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Состояния для модального окна создания/редактирования пользователя
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    roleId: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  
  // Состояние для модального окна подтверждения действий
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Загрузка пользователей при изменении страницы
  useEffect(() => {
    fetchUsers(page);
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
  }, [page]);
  
  // Загрузка ролей при монтировании компонента
  useEffect(() => {
    fetchRoles();
  }, []);
  
  const filtersInitialized = useRef(false);

  // Загрузка пользователей при изменении фильтров с задержкой
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(setTimeout(() => {
      setPage(1);
      fetchUsers(1);
    }, 500));

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, roleFilter, statusFilter]);
  
  /**
   * Загрузка списка ролей с сервера
   */
  const fetchRoles = async () => {
    try {
      let rolesList = [];
      
      // Проверяем наличие метода getRoles
      if (typeof userService.getRoles === 'function') {
        rolesList = await userService.getRoles();
      } else {
        // Используем встроенный список ролей, если метод не доступен
        console.warn('Метод userService.getRoles не доступен, используем запасной список ролей');
        rolesList = [
          { id: 1, name: 'admin', description: 'Администратор' },
          { id: 2, name: 'manager', description: 'Менеджер' },
          { id: 3, name: 'operator', description: 'Оператор' },
          { id: 4, name: 'analyst', description: 'Аналитик' }
        ];
      }
      
      setRoles(rolesList);
    } catch (err) {
      console.error('Ошибка при загрузке ролей:', err);
      toast.error('Не удалось загрузить список ролей');
      
      // Используем запасной список
      setRoles([
        { id: 1, name: 'admin', description: 'Администратор' },
        { id: 2, name: 'manager', description: 'Менеджер' },
        { id: 3, name: 'operator', description: 'Оператор' },
        { id: 4, name: 'analyst', description: 'Аналитик' }
      ]);
    }
  };
  
  /**
   * Загрузка пользователей с сервера
   * @param {number} pageNumber - Номер страницы
   */
  const fetchUsers = async (pageNumber) => {
    setLoading(true);
    setError(null);
    
    try {
      // Получаем пользователей через сервис
      const response = await userService.getUsers(pageNumber, 10);
      
      // Проверяем, есть ли данные в ответе
      if (response && Array.isArray(response.data)) {
        // Применяем фильтры на стороне клиента, если API не поддерживает их
        let filteredUsers = response.data;
        
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          filteredUsers = filteredUsers.filter(user => 
            user.username.toLowerCase().includes(searchLower) || 
            user.fullName.toLowerCase().includes(searchLower) || 
            user.email.toLowerCase().includes(searchLower)
          );
        }
        
        if (roleFilter) {
          filteredUsers = filteredUsers.filter(user => 
            user.role?.toLowerCase() === roleFilter.toLowerCase()
          );
        }
        
        if (statusFilter) {
          filteredUsers = filteredUsers.filter(user => 
            user.status?.toLowerCase() === statusFilter.toLowerCase()
          );
        }
        
        setUsers(filteredUsers);
        setTotalPages(response.totalPages || 1);
        setPage(pageNumber);
      } else {
        console.warn('Некорректный формат ответа:', response);
        setUsers([]);
        setError('Получены некорректные данные с сервера');
      }
    } catch (err) {
      console.error('Ошибка при загрузке пользователей:', err);
      setError('Не удалось загрузить пользователей. Пожалуйста, попробуйте позже.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Открытие модального окна для создания пользователя
   */
  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      roleId: ''
    });
    setFormErrors({});
    setShowModal(true);
  };
  
  // Открытие модального окна для редактирования пользователя
  const handleEditUser = async (userId) => {
    try {
      setProcessing(true);
      const userData = await userService.getUserById(userId);
      
      const findRoleId = (roleName) => {
        if (roleName && roles.length > 0) {
          const role = roles.find(r => r.name?.toLowerCase() === roleName.toLowerCase());
          return role ? role.id : '';
        }
        return '';
      };
      
      setEditingUser(userData);
      setFormData({
        username: userData.username || '',
        fullName: userData.fullName || '',
        email: userData.email || '',
        password: '',
        confirmPassword: '',
        roleId: findRoleId(userData.role)
      });
      
      setFormErrors({});
      setShowModal(true);
    } catch (err) {
      console.error('Ошибка при получении данных пользователя:', err);
      toast.error('Не удалось загрузить данные пользователя');
    } finally {
      setProcessing(false);
    }
  };
  
  /**
   * Закрытие модального окна
   */
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };
  
  /**
   * Обработка изменения полей формы
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Сбрасываем ошибку для измененного поля
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  /**
   * Валидация формы перед отправкой
   */
  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = 'Введите имя пользователя';
    }
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Введите полное имя';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Введите email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Введите корректный email';
    }
    
    if (!editingUser && !formData.password) {
      errors.password = 'Введите пароль';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'Пароль должен содержать не менее 6 символов';
    }
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают';
    }
    
    if (!formData.roleId) {
      errors.roleId = 'Выберите роль';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  /**
   * Отправка формы создания/редактирования пользователя
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setProcessing(true);
    
    try {
      const userData = {
        username: formData.username,
        fullName: formData.fullName,
        email: formData.email,
        roleId: parseInt(formData.roleId, 10)
      };
      
      // Добавляем пароль только если он указан
      if (formData.password) {
        userData.password = formData.password;
      }
      
      if (editingUser) {
        // Обновление существующего пользователя
        await userService.updateUser(editingUser.id, userData);
        toast.success('Пользователь успешно обновлен');
      } else {
        // Создание нового пользователя
        await userService.createUser(userData);
        toast.success('Пользователь успешно создан');
      }
      
      handleCloseModal();
      fetchUsers(page);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingUser ? 'Не удалось обновить пользователя' : 'Не удалось создать пользователя');
      
      toast.error(errorMessage);
      console.error('Ошибка при сохранении пользователя:', err);
    } finally {
      setProcessing(false);
    }
  };
  
  /**
   * Открытие диалога подтверждения изменения статуса пользователя
   */
  const confirmToggleUserStatus = (userId, currentStatus, userName) => {
    const isActivating = currentStatus.toLowerCase() !== 'active';
    const actionText = isActivating ? 'активировать' : 'деактивировать';
    
    setConfirmAction({
      title: `${isActivating ? 'Активация' : 'Деактивация'} пользователя`,
      message: `Вы действительно хотите ${actionText} пользователя "${userName}"?`,
      onConfirm: () => handleToggleUserStatus(userId, currentStatus),
      confirmText: isActivating ? 'Активировать' : 'Деактивировать',
      confirmClass: isActivating ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
    });
  };
  
  /**
   * Изменение статуса пользователя
   */
  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      setProcessing(true);
      
      // Определение нового статуса
      const lowerStatus = currentStatus.toLowerCase();
      const newStatus = lowerStatus === 'active' ? 'Inactive' : 'Active';
      
      await userService.updateUserStatus(userId, newStatus);
      
      toast.success(`Статус пользователя успешно ${newStatus === 'Active' ? 'активирован' : 'деактивирован'}`);
      fetchUsers(page);
      setConfirmAction(null);
    } catch (err) {
      console.error('Ошибка при изменении статуса пользователя:', err);
      toast.error('Не удалось изменить статус пользователя');
    } finally {
      setProcessing(false);
    }
  };
  
  /**
   * Получение имени роли по ID
   */
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === parseInt(roleId, 10));
    return role ? (role.description || role.name) : '';
  };
  
  /**
   * Генерация случайного надежного пароля
   * @param {number} length - Длина пароля (по умолчанию 10)
   * @returns {string} - Сгенерированный пароль
   */
  const generatePassword = (length = 10) => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()-_=+[]{}|;:,.<>?';
    
    const allChars = lowercase + uppercase + numbers + symbols;
    
    // Гарантируем, что пароль будет содержать как минимум по одному символу из каждой категории
    let password = '';
    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += symbols.charAt(Math.floor(Math.random() * symbols.length));
    
    // Заполняем остальную часть пароля случайными символами
    for (let i = 4; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    // Перемешиваем символы в пароле
    password = password.split('').sort(() => 0.5 - Math.random()).join('');
    
    return password;
  };
  
  /**
   * Обработчик генерации пароля
   */
  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData({
      ...formData,
      password: newPassword,
      confirmPassword: newPassword
    });
    
    // Сбрасываем ошибки для полей пароля
    if (formErrors.password || formErrors.confirmPassword) {
      setFormErrors({
        ...formErrors,
        password: '',
        confirmPassword: ''
      });
    }
  };
  
  /**
   * Переключение видимости пароля
   */
  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };
  
  /**
   * Форматирование даты и времени
   */
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Никогда';
    return new Date(dateString).toLocaleString('ru-RU');
  };
  
  return (
    <div>
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Пользователи</h1>
          <p className="text-gray-600">Управление пользователями системы</p>
        </div>
        
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleCreateUser}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            {getIcon('plus')}
            <span className="ml-2">Добавить пользователя</span>
          </button>
        </div>
      </header>
      
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex-1 mb-4 md:mb-0">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {getIcon('search')}
                </div>
                <input
                  type="text"
                  placeholder="Поиск по имени или email..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
              <select
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">Все роли</option>
                <option value="admin">Администратор</option>
                <option value="manager">Менеджер</option>
                <option value="operator">Оператор</option>
                <option value="analyst">Аналитик</option>
              </select>
              
              <select
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Все статусы</option>
                <option value="active">Активен</option>
                <option value="inactive">Неактивен</option>
                <option value="blocked">Заблокирован</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Отображение ошибки если есть */}
        {error && (
          <div className="p-4 bg-red-50 text-red-700 border-b border-gray-200">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>{error}</p>
            </div>
            <button 
              className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              onClick={() => fetchUsers(page)}
            >
              Попробовать еще раз
            </button>
          </div>
        )}
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-gray-500 mb-4">Пользователи не найдены</p>
              <button
                onClick={() => fetchUsers(1)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Обновить
              </button>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Имя пользователя
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Полное имя
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Роль
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Последний вход
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.fullName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.role}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.status === 'Active' || user.status === 'active' ? 'bg-green-100 text-green-800' :
                        user.status === 'Inactive' || user.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {(user.status === 'Active' || user.status === 'active') ? 'Активен' :
                         (user.status === 'Inactive' || user.status === 'inactive') ? 'Неактивен' :
                         'Заблокирован'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {formatDateTime(user.lastLoginAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleEditUser(user.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Редактировать"
                          disabled={processing}
                        >
                          {getIcon('edit')}
                        </button>
                        <button 
                          onClick={() => confirmToggleUserStatus(
                            user.id, 
                            user.status, 
                            user.username
                          )}
                          className="text-red-600 hover:text-red-900"
                          title={(user.status === 'Active' || user.status === 'active') ? 'Деактивировать' : 'Активировать'}
                          disabled={processing}
                        >
                          {getIcon((user.status === 'Active' || user.status === 'active') ? 'block' : 'checkmark')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Страница {page} из {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => page > 1 && setPage(page - 1)}
                disabled={page === 1}
                className={`px-3 py-1 rounded-md ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
              >
                Назад
              </button>
              <button
                onClick={() => page < totalPages && setPage(page + 1)}
                disabled={page === totalPages}
                className={`px-3 py-1 rounded-md ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
              >
                Далее
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Модальное окно создания/редактирования пользователя */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg max-w-xl w-full mx-auto shadow-xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'Редактирование пользователя' : 'Создание нового пользователя'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
                disabled={processing}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Имя пользователя *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`border ${formErrors.username ? 'border-red-500' : 'border-gray-300'} rounded-md w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  disabled={editingUser}
                />
                {formErrors.username && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.username}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Полное имя *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`border ${formErrors.fullName ? 'border-red-500' : 'border-gray-300'} rounded-md w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {formErrors.fullName && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.fullName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-md w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editingUser ? 'Новый пароль (оставьте пустым, чтобы не менять)' : 'Пароль *'}
                </label>
                <div className="relative">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`border ${formErrors.password ? 'border-red-500' : 'border-gray-300'} rounded-md w-full py-2 pl-3 pr-24 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="px-2 py-1 h-full text-gray-600 hover:text-gray-800 focus:outline-none"
                      title={passwordVisible ? "Скрыть пароль" : "Показать пароль"}
                    >
                      {passwordVisible ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                    <div className="h-5 w-px bg-gray-300 mx-1"></div>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="px-2 py-1 h-full text-blue-600 hover:text-blue-800 focus:outline-none"
                      title="Сгенерировать пароль"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  </div>
                </div>
                {formErrors.password && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.password}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Подтверждение пароля
                </label>
                <div className="relative">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`border ${formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-md w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
                {formErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.confirmPassword}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Роль *
                </label>
                <select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleChange}
                  className={`border ${formErrors.roleId ? 'border-red-500' : 'border-gray-300'} rounded-md w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="">Выберите роль</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.description}</option>
                  ))}
                </select>
                {formErrors.roleId && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.roleId}</p>
                )}
              </div>
              
              <div className="flex justify-end pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
                  disabled={processing}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Сохранение...
                    </>
                  ) : (
                    'Сохранить'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Модальное окно подтверждения действия */}
      {confirmAction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg max-w-md w-full mx-auto shadow-xl p-6">
            <div className="flex flex-col items-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {confirmAction.title}
              </h3>
              <p className="text-gray-500 text-center mb-6">
                {confirmAction.message}
              </p>
              <div className="flex justify-center space-x-4 w-full">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                  disabled={processing}
                >
                  Отмена
                </button>
                <button
                  onClick={confirmAction.onConfirm}
                  className={`${confirmAction.confirmClass} text-white font-bold py-2 px-4 rounded flex items-center`}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Обработка...
                    </>
                  ) : (
                    confirmAction.confirmText
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;